/**
 * Translation keys for the CVS Account Preferences application.
 * These keys are used with the @cvsdigital_web/react-cmk-i18n library.
 *
 * SECURITY NOTE: Never include PHI/PII/HIPAA data in translation keys.
 * Use tokenization for dynamic content (e.g., ${userName}).
 */

export const translations = {
  // Page titles
  'page.title': 'Communication preferences',
  'page.texts.title': 'Text alerts',
  'page.automatedCalls.title': 'Automated calls',
  'page.emails.title': 'Emails',
  'page.mail.title': 'Mail',

  // Preference types
  'preference.types.texts': 'Texts',
  'preference.types.automatedCalls': 'Automated calls',
  'preference.types.emails': 'Emails',
  'preference.types.emailsAndReceipts': 'Emails and receipts',
  'preference.types.mail': 'Mail',
  'preference.types.mailAndPrint': 'Mail and print',

  // Success messages
  'preference.update.success.heading': 'All set',
  'preference.update.success.body': 'Your information has been updated.',

  // Text Communication
  'texts.pharmacy.label': 'Pharmacy text alerts',
  'texts.extracare.label': 'Extracare deals and rewards',
  'texts.caremark.label': 'CVS Caremark®',
  'texts.specialty.label': 'CVS Specialty®',

  // Pharmacy Text Alerts
  'pharmacy.texts.title': 'Pharmacy texts',
  'pharmacy.texts.subtitle': 'All fields are required.',
  'pharmacy.texts.mobileLabel': 'Mobile number',
  'pharmacy.texts.mobileHelper': 'Enter a 10-digit number',
  'pharmacy.texts.enrollmentHeading': 'What type of pharmacy texts do you want?',
  'pharmacy.texts.enrollmentHelper': 'Enrollment is optional and you can opt out at any time.',
  'pharmacy.texts.disclaimer':
    "If you're enrolled in both texts and calls with the same mobile number, the message type you select for texts will also apply to calls.",
  'pharmacy.texts.smsDisclaimer':
    'By selecting to enroll in text messages, I agree to receive messages from CVS Pharmacy based on the message category selected above. Message frequency varies. Message & data rates may apply. Reply STOP to stop; UNDO to re-enroll; HELP for help. See <a href="https://www.cvs.com/retail/help/terms_of_use" target="_blank">Terms of Use</a> and <a href="https://www.cvs.com/retail/help/privacy_policy" target="_blank">Privacy Policy</a>.',

  // Specialty Text Alerts
  'specialty.texts.title': 'Specialty texts',
  'specialty.texts.subtitle': 'All fields are required.',
  'specialty.texts.mobileLabel': 'Mobile number',
  'specialty.texts.mobileHelper': 'Enter a 10-digit number',
  'specialty.texts.enrollmentHeading': 'Choose a specialty text option',
  'specialty.texts.enrollmentHelper': 'Enrollment is optional and you can opt out at any time.',

  // ExtraCare Text Alerts
  'extracare.texts.title': 'ExtraCare® texts',
  'extracare.texts.subtitle': 'All fields are required.',
  'extracare.texts.mobileLabel': 'Mobile number',
  'extracare.texts.mobileHelper': 'Enter a 10-digit number',
  'extracare.texts.enrollmentHeading': 'Choose an ExtraCare text option',
  'extracare.texts.enrollmentHelper': 'Enrollment is optional and you can opt out at any time.',
  'extracare.texts.info': 'To make changes to your ExtraCare text preferences, call 1-800-SHOP-CVS',
  'extracare.texts.customerCareNumber': '(1-800-746-7287)',
  'extracare.texts.enrollmentMsg': 'You are enrolled in ExtraCare texts at',

  // Caremark Texts
  'caremark.texts.title': 'Caremark texts',
  'caremark.texts.subtitle': 'All fields are required. Additional fields may appear based on your selection.',
  'caremark.texts.phoneLabel': 'Mobile number',
  'caremark.texts.phoneHelper': 'Enter a 10-digit number',
  'caremark.texts.enrollOptionLegend': 'Choose a Caremark text option',
  'caremark.texts.enrollOptionHelper': 'Enrollment is optional and you can opt out at any time.',
  'caremark.texts.optionsLegend': 'What type of texts do you want?',
  'caremark.texts.optionsHelper': 'Choose 1 or more.',

  // Automated Calls
  'calls.pharmacy.label': 'Pharmacy calls',
  'calls.caremark.label': 'CVS Caremark®',
  'calls.pharmacy.title': 'Pharmacy calls',
  'calls.pharmacy.enrollmentHeading': 'What type of pharmacy calls do you want?',

  // Caremark Calls
  'caremark.calls.title': 'Caremark calls',
  'caremark.calls.subtitle': 'All fields are required. Additional fields may appear based on your selection.',
  'caremark.calls.phoneLabel': 'Phone number',
  'caremark.calls.phoneHelper': 'Enter a 10-digit number',
  'caremark.calls.enrollOptionLegend': 'Choose a Caremark calls option',
  'caremark.calls.enrollOptionHelper': 'Enrollment is optional and you can opt out at any time.',
  'caremark.calls.optionsLegend': 'What type of calls do you want?',
  'caremark.calls.optionsHelper': 'Choose 1 or more.',

  // Emails
  'emails.cvs.label': 'CVS.com online deals',
  'emails.pharmacy.label': 'Pharmacy emails',
  'emails.extracare.label': 'ExtraCare personalized offers and in-store receipts',
  'emails.specialty.label': 'CVS Specialty®',
  'emails.caremark.label': 'CVS Caremark®',
  'emails.health100.label': 'Health100™',
  'emails.alerts.legendText': 'Choose an email subscription option.',
  'emails.alerts.helperText': 'Enrollment is optional and you can opt out at any time.',

  // Pharmacy Emails
  'pharmacy.emails.title': 'Pharmacy emails',
  'pharmacy.emails.subtitle': 'All fields are required.',
  'pharmacy.emails.emailLabel': 'Email address',
  'pharmacy.emails.emailHelper': 'Enter an email address',
  'pharmacy.emails.subscriptionHeading': 'Choose an email subscription option.',
  'pharmacy.emails.subscriptionHelper': 'Enrollment is optional and you can opt out at any time.',

  // Specialty Emails
  'specialty.emails.title': 'Specialty emails',
  'specialty.emails.subtitle': 'All fields are required.',
  'specialty.emails.emailLabel': 'Email address',
  'specialty.emails.emailHelper': "Enter an email address. It won't change your CVS Specialty sign-in email address.",
  'specialty.emails.subscriptionHeading': 'Choose a specialty email option.',
  'specialty.emails.subscriptionHelper': 'Enrollment is optional and you can opt out at any time.',

  // Caremark Emails
  'caremark.emails.title': 'Caremark emails',
  'caremark.emails.subtitle': 'All fields are required. Additional fields may appear based on your selection.',
  'caremark.emails.emailLabel': 'Email address',
  'caremark.emails.emailHelper': 'Enter an email address',
  'caremark.emails.subscriptionHeading': 'Choose a Caremark calls option',
  'caremark.emails.subscriptionHelper': 'Enrollment is optional and you can opt out at any time.',
  'caremark.emails.legendText': 'What types of emails do you want?',
  'caremark.emails.helperText': 'Choose 1 or more.',

  // ExtraCare Emails
  'extracare.emails.title': 'ExtraCare® emails',
  'extracare.emails.subtitle': 'All fields are required.',
  'extracare.emails.emailLabel': 'Email address',
  'extracare.emails.emailHelper': 'Enter an email address',
  'extracare.emails.subscriptionHeading': 'Choose an email subscription option',
  'extracare.emails.subscriptionHelper': 'Enrollment is optional and you can opt out at any time.',
  'extracare.emails.instoreReceiptsHeading': 'In-store receipts',
  'extracare.emails.instoreReceiptsHelper': 'How do you want to get receipts when you shop in person?',
  'extracare.emails.info': 'To make changes to your ExtraCare email preferences, call 1-800-SHOP-CVS ',

  // CVS.com Emails
  'cvs.emails.title': 'CVS.com emails',
  'cvs.emails.subtitle': 'All fields are required.',

  // H100 Emails
  'h100.emails.title': 'Health100™ emails',
  'h100.emails.subtitle': 'All fields are required.',
  'h100.email.alert.legendText': 'Email preferences',
  'h100.email.alert.helperText': 'You can opt out at any time.',

  // Mail
  'mail.extracare.label': 'ExtraCare® offers',
  'mail.extracare.title': 'ExtraCare® mail',
  'mail.extracare.subtitle': 'All fields are required.',
  'mail.extracare.primaryAddressLabel': 'Primary address',
  'mail.extracare.subscriptionHeading': 'Choose a mail subscription option.',
  'mail.extracare.subscriptionHelper': 'Enrollment is optional and you can opt out at any time.',
  'mail.extracare.info': 'To make changes to your ExtraCare mail preferences, call 1-800-SHOP-CVS ',
  'mail.extracare.customerCareNumber': '(1-800-746-7287)',
  'mail.extracare.customerCareNumberLink': 'tel:+18007467287',

  // Common buttons
  'button.cancel': 'Cancel',
  'button.save': 'Save',
  'button.saveChanges': 'Save changes',
  'button.edit': 'Edit',

  // Common form labels
  'form.mobileLabel': 'Mobile',
  'form.phoneNumberLabel': 'Phone number',
  'form.phoneTypeLabel': 'Phone type',
  'form.accountEmailLabel': 'Account email address',

  // Leave modal defaults
  'modal.leave.heading': 'Leave without saving?',
  'modal.leave.body': 'Your changes will be lost',
  'modal.leave.submitBtn': 'Leave',
  'modal.leave.cancelBtn': 'Keep editing',
  'modal.savingChanges': 'Saving changes',

  // Phone conflict modal
  'modal.phoneConflict.heading': 'Pharmacy can only have one mobile number',
  'modal.phoneConflict.bodyP1': "A mobile number is already connected to this account. There can't be more than one.",
  'modal.phoneConflict.bodyP2':
    'If you choose {{NUMBER}} as your new mobile phone number, your text alert and automated call settings will also be updated. You can go back and change your mobile phone number at any time.',
  'modal.phoneConflict.submitBtn': 'Use {{NUM}} ',
  'modal.phoneConflict.keepExistingBtn': 'Keep existing number',

  // Caremark phone conflict
  'modal.cmkPhoneConflict.heading': 'Choose a mobile number',
  'modal.cmkPhoneConflict.desc':
    'You can have only one mobile number for CVS Caremark communications. The mobile number will apply to Caremark texts and calls.',
  'modal.cmkPhoneConflict.submit': 'Use',
  'modal.cmkPhoneConflict.decline': 'Keep existing number',

  // Validation messages
  'validation.phone.invalid': 'Enter a valid 10-digit mobile number',
  'validation.phone.makeSelection': 'Make a selection',
  'validation.email.required': 'Enter an email address',
  'validation.email.invalid': 'Enter a valid email address',
  'validation.email.maxChars': 'Email must be 40 characters or less',
  'validation.makeSelection': 'Make a selection',
  'validation.cmkTexts.makeSelection': 'Select 1 or more type of texts to enroll',
  'validation.cmkCalls.makeSelection': 'Select 1 or more type of calls to enroll',
  'validation.cmkEmails.makeSelection': 'Select 1 or more type of emails to enroll',

  // Error messages
  'error.update.heading': 'Something went wrong on our end',
  'error.update.body': "Sorry, we couldn't update your notifications.",
  'error.xlob.title': 'Error Updating Preferences',
  'error.xlob.body': 'There was an error updating your specialty communication preferences. Please try again later.',
  'error.failure.title': 'Please try that again',
  'error.failure.message': 'Something went wrong on our end',
  'error.formSubmit.title': 'Changes not saved',
  'error.formSubmit.message': 'Check your entries and try again.',

  // Error page
  'error.page.header': 'Something went wrong on our end',
  'error.page.body': "Sorry, it looks like we're having technical issues. Please try again later.",

  // Subscription options - Pharmacy texts
  'subscription.pharmacy.texts.allEnroll.title': 'Enroll in all pharmacy texts',
  'subscription.pharmacy.texts.allEnroll.desc':
    'Receive automated texts from CVS Pharmacy about prescriptions, marketing, and other health related information.',
  'subscription.pharmacy.texts.prescriptionOnly.title': 'Enroll in prescription texts',
  'subscription.pharmacy.texts.prescriptionOnly.desc':
    'Receive automated texts from CVS Pharmacy about your prescriptions, such as Order Ready, Delivery, Refill alerts, and other health related information',
  'subscription.pharmacy.texts.none.title': "Don't enroll in automated texts at this time",
  'subscription.pharmacy.texts.none.desc': "You won't receive automated texts from CVS Pharmacy",

  // Subscription options - Specialty texts
  'subscription.specialty.texts.enroll.label': 'Enroll in specialty texts.',
  'subscription.specialty.texts.enroll.desc':
    'Get CVS Specialty texts for prescriptions, orders, payments, marketing and other prescription and health-related information.',
  'subscription.specialty.texts.noEnroll.label': "Don't enroll in specialty texts.",
  'subscription.specialty.texts.noEnroll.desc': "You won't get texts from CVS Specialty.",

  // Subscription options - ExtraCare texts
  'subscription.extracare.texts.enroll.label': 'Enroll in ExtraCare texts.',
  'subscription.extracare.texts.enroll.desc':
    'Get marketing texts about deals, rewards, membership details and new services.',
  'subscription.extracare.texts.noEnroll.label': "Don't enroll in texts at this time.",
  'subscription.extracare.texts.noEnroll.desc': "You won't get ExtraCare marketing texts.",

  // Subscription options - Pharmacy calls
  'subscription.pharmacy.calls.prescriptionOnly.title': 'Enroll in prescription-only calls.',
  'subscription.pharmacy.calls.prescriptionOnly.desc':
    'Get calls from CVS Pharmacy about your prescriptions, such as Order Ready, Delivery, Refill alerts​ and other health-related information.',
  'subscription.pharmacy.calls.allEnroll.title': 'Enroll in prescription and marketing calls.',
  'subscription.pharmacy.calls.allEnroll.desc':
    'Get calls from CVS Pharmacy about prescriptions, marketing and other health-related information.',
  'subscription.pharmacy.calls.none.title': "Don't enroll in any calls.",
  'subscription.pharmacy.calls.none.desc': "You won't get calls from CVS Pharmacy.",

  // Subscription options - Pharmacy emails
  'subscription.pharmacy.emails.enroll.desc':
    'Enroll to get pharmacy emails about your prescriptions, health care and related services.',
  'subscription.pharmacy.emails.noEnroll.desc': "Don't enroll at this time. You won't receive pharmacy emails.",

  // Subscription options - Specialty emails
  'subscription.specialty.emails.enroll.label': 'Enroll in specialty emails.',
  'subscription.specialty.emails.enroll.desc':
    'Get CVS Specialty emails for prescriptions, orders, payments, marketing and other prescription and health-related information.',
  'subscription.specialty.emails.noEnroll.label': "Don't enroll in specialty emails.",
  'subscription.specialty.emails.noEnroll.desc': "You won't get email from CVS Specialty.",

  // Subscription options - Caremark emails
  'subscription.caremark.emails.enroll.label': 'Enroll in Caremark email.',
  'subscription.caremark.emails.enroll.desc':
    'Get emails for refill reminders, benefit updates, bill payments, mail order statuses and other health-related information.',
  'subscription.caremark.emails.noEnroll.label': "Don't enroll in email at this time.",
  'subscription.caremark.emails.noEnroll.desc': "You won't get emails from Caremark.",

  // Subscription options - Caremark texts
  'subscription.caremark.texts.enroll.label': 'Enroll in Caremark texts.',
  'subscription.caremark.texts.enroll.desc':
    'Get texts for refill reminders, benefit updates, bill payments, marketing, mail order statuses and other health-related information.',
  'subscription.caremark.texts.noEnroll.label': "Don't enroll in texts at this time.",
  'subscription.caremark.texts.noEnroll.desc': "You won't get texts from Caremark.",

  // Subscription options - Caremark calls
  'subscription.caremark.calls.enroll.label': 'Enroll in Caremark phone calls.',
  'subscription.caremark.calls.enroll.desc':
    'Get calls for refill reminders, benefit updates, bill payments, marketing, mail order statuses and other health-related information.',
  'subscription.caremark.calls.noEnroll.label': "Don't enroll in calls at this time.",
  'subscription.caremark.calls.noEnroll.desc': "You won't get calls from Caremark.",

  // Subscription options - ExtraCare mail
  'subscription.extracare.mail.enroll.desc':
    'Enroll to have personalized ExtraCare offers mailed to your primary address.',
  'subscription.extracare.mail.noEnroll.desc': "Don't enroll at this time. You won't get mailed ExtraCare offers.",

  // Subscription options - ExtraCare emails
  'subscription.extracare.emails.enroll.desc': 'Enroll to receive personalized ExtraCare offers.',
  'subscription.extracare.emails.noEnroll.desc': "Don't enroll. You won't receive ExtraCare offers.",

  // Subscription options - CVS.com emails
  'subscription.cvs.emails.enroll.desc': 'Enroll to get CVS.com deals and offers.',
  'subscription.cvs.emails.noEnroll.desc':
    "Don't enroll in email at this time. You won't receive CVS.com deals and offers.",

  // Subscription options - Health100 emails
  'subscription.h100.emails.enroll.desc': 'Receive Health100 news, exclusive offers and product updates.',
  'subscription.h100.emails.noEnroll.desc': 'Not now.',

  // In-store receipts options
  'receipts.alwaysEmail.desc': 'Always email',
  'receipts.emailAndPrint.desc': 'Always email and print',
  'receipts.chooseAtCheckout.desc': 'Let me choose at checkout',

  // Phone type options
  'phoneType.select': 'Select an option',
  'phoneType.mobile': 'Mobile',
  'phoneType.home': 'Home',
  'phoneType.office': 'Office',
  'phoneType.other': 'Other',
  'caremark.calls.phoneTypeLabel': 'Phone type',

  // Customer care
  'customerCare.number': '(1-800-746-7287)',
  'customerCare.numberLink': 'tel:+18007467287',

  // ExtraCare texts - customer care
  'extracare.texts.customerCareNumberLink': 'tel:+18007467287',

  // ExtraCare emails - customer care
  'extracare.emails.customerCareNumber': '(1-800-746-7287)',
  'extracare.emails.customerCareNumberLink': 'tel:+18007467287',

  // Preference status tag labels
  'preference.status.on.label': 'ON',
  'preference.status.off.label': 'OFF',

  // Breadcrumb navigation
  'breadcrumb.home': 'Home',
  'breadcrumb.account': 'Account',
  'breadcrumb.back': 'Back',
  'breadcrumb.commPrefs': 'Communication preferences',
  'breadcrumb.texts': 'Texts',
  'breadcrumb.automatedCalls': 'Automated calls',
  'breadcrumb.emails': 'Emails',
  'breadcrumb.mail': 'Mail',

  // Coming soon
  'comingSoon.title': 'Coming soon',
  'comingSoon.description': 'You’ll be able to manage Health100 communication preferences in a future update.',

  // Spinner labels
  'spinner.title': 'Processing request',
  'spinner.body': 'This may take a little time',
};

export type TranslationKey = keyof typeof translations;
